import React from 'react';
import { Tooltip } from 'react-tooltip';

import Text from 'components/Text';
import ModelButton from 'components/ModelButton';
import { InputThemer, OneLineText } from 'components/OpenProtocolOrderItems/styles';
import {
  boletoAction,
  comprovanteDeEntregaAction,
  eventName,
  fichaTecnicaAction,
  laudoTecnicoAction,
  notaCorrecaoAction,
  notaFiscalAction,
  pedidosAction
} from 'components/ApplicationInsights/eventNames';

import iconTracking from 'img/icon-tracking.svg';
import iconTrackingOrder from 'img/icon-tracking-order.svg';
import iconNoteCorrection from 'img/icon-correction-slider.svg';
import iconBoleto from 'img/icon-boleto.svg';
import iconDownload from 'img/icon-download.svg';
import iconRecord from 'img/icon-record.svg';
import iconRecordExpressa from 'img/icon-record-expressa.svg';
import iconXml from 'img/icon-xml.svg';
import iconGreenDelivery from 'img/icon-green-delivery.svg';
import iconClockDeliveryForecast from 'img/icon-clock-delivery-forecast.svg';

import { IOrders, IDownloadTypes, IOrderItem } from 'models/Order';
import { IUser } from 'models/User';

import DownloadService from 'services/download.service';
import CreateProtocolServices from 'services/createProtocol.service';

import { ITableModel } from '../interface';

import { Capitalize } from 'utils/String';
import { formatDocument } from 'utils/Document';
import TicketRule from 'services/Ticket/rule.service';

const _downloadService = new DownloadService();
const _createProtocolServices = new CreateProtocolServices();

const pendingFilter = {
  billingDate: { eq: null },
  items: { any: { orderItemStatusType: { eq: 'PENDING' } } },
};
const billedFilter = {
  billingDate: { ne: null },
  items: { any: { orderItemStatusType: { eq: 'PERFORMED' } } },
};
const shippingFilter = {
  invoiceShipping: {
    and: [
      { invoiceNumber: { ne: '' } },
      { invoiceNumber: { ne: null } }
    ]
  }
};

const billetActionButton = (title?: string, size?: number, user?: IUser | undefined) =>
  ModelButton(
    title || '',
    iconBoleto,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.BILLET, data, user?.userType),
    eventName.BOLETO,
    size,
    window.location.pathname === '/pedidos' ?
      boletoAction.PEDIDO_BOLETO :
      boletoAction.DASHBOARD_BOLETO
  );

const billetActionButtonSlider = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconBoleto,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.BILLET, data),
    eventName.BOLETO,
    size,
    boletoAction.DASHCLIENTE_BOLETO
  );

const invoiceActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconDownload,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE, data),
    eventName.NOTA_FISCAL,
    size,
    window.location.pathname === '/pedidos' ?
      notaFiscalAction.PEDIDO_NOTAFISCAL_PDF :
      notaFiscalAction.DASHBOARD_NOTAFISCAL_PDF
  );

const deliveryForecastButon = (title?: string, size?: number, user?: IUser | undefined) =>
  ModelButton(
    title || '',
    iconClockDeliveryForecast,
    (data: IOrders) => _createProtocolServices.createProtocol(data, user),
    eventName.PREVISAO_DE_ENTREGA,
    size
  );

const notaFiscalActionPDF = () => {
  if (window.location.pathname === '/pedidos') {
    return notaFiscalAction.PEDIDO_NOTAFISCAL_REMESSA_PDF;
  }

  return window.location.pathname === '/cliente' ?
    notaFiscalAction.COCKPIT_NOTAFISCAL_REMESSA_PDF :
    notaFiscalAction.DASHBOARD_NOTAFISCAL_REMESSA_PDF;
};

const notaFiscalActionXML = () => {
  if (window.location.pathname === '/pedidos')
    return notaFiscalAction.PEDIDO_NOTAFISCAL_REMESSA_XML;

  return window.location.pathname === '/cliente' ?
    notaFiscalAction.COCKPIT_NOTAFISCAL_REMESSA_XML :
    notaFiscalAction.DASHBOARD_NOTAFISCAL_REMESSA_XML;
};

const comprovanteEntregaActionRemessa = () => {
  if (window.location.pathname === '/pedidos')
    return comprovanteDeEntregaAction.PEDIDO_COMPROVANTEENTREGA_REMESSA;

  return window.location.pathname === '/cliente' ?
    comprovanteDeEntregaAction.COCKPIT_COMPROVANTEENTREGA_REMESSA :
    comprovanteDeEntregaAction.DASHCLIENTE_COMPROVANTEENTREGA_REMESSA;
};

const invoiceShippingActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconDownload,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_SHIPPING, data),
    eventName.NOTA_FISCAL_REMESSA,
    size,
    notaFiscalActionPDF()
  );

const invoiceActionButonSlider = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconDownload,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE, data),
    eventName.NOTA_FISCAL,
    size,
    notaFiscalAction.DASHCLIENTE_NOTAFISCAL_PDF
  );

const xmlActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconXml,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_XML, data),
    eventName.NOTA_FISCAL,
    size,
    window.location.pathname === '/pedidos' ?
      notaFiscalAction.PEDIDO_NOTAFISCAL_XML :
      notaFiscalAction.DASHBOARD_NOTAFISCAL_XML
  );

const xmlShippingActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconXml,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.INVOICE_SHIPPING_XML, data),
    eventName.NOTA_FISCAL_REMESSA,
    size,
    notaFiscalActionXML()
  );

const trackingActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconTracking,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.RECEIPT, data),
    eventName.COMPROVANTE_DE_ENTREGA,
    size,
    comprovanteDeEntregaAction.PEDIDO_COMPROVANTEENTREGA,
    '',
    undefined,
    'left'
  );

const trackingShippingActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconTracking,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.RECEIPT_SHIPPING, data),
    eventName.COMPROVANTE_DE_ENTREGA_REMESSA,
    size,
    comprovanteEntregaActionRemessa(),
    '',
    undefined,
    'left'
  );

const trackingActionButonSlider = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconTracking,
    (data: IOrders) =>
      _downloadService.OpenFileFromOrder(IDownloadTypes.RECEIPT, data),
    eventName.COMPROVANTE_DE_ENTREGA,
    size,
    comprovanteDeEntregaAction.DASHCLIENTE_COMPROVANTEENTREGA,
    '',
    undefined,
    'left'
  );

const noteCorrectionActionButonSlider = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconNoteCorrection,
    (data: IOrderItem) => data,
    eventName.NOTA_DE_CORRECAO,
    size,
    notaCorrecaoAction.DASHCLIENTE_NOTADECORRECAO,
    '',
    undefined,
    'left',
    true
  );

const trackingOrderActionButon = (title?: string, size?: number) =>
  ModelButton(
    title || '',
    iconTrackingOrder,
    () => null,
    eventName.TRACKING_PEDIDO,
    size,
    window.location.pathname === '/pedidos' ?
      pedidosAction.VISUALIZARPEDIDO :
      pedidosAction.DASHBOARD_VISUALIZARPEDIDO,
    '',
    undefined,
    'left'
  );

const recordActionButon = ModelButton(
  'Ficha técnica',
  iconRecord,
  (data: IOrderItem) =>
    _downloadService.OpenFileFromOrderItem(IDownloadTypes.RECORD, data),
  eventName.FICHA_TECNICA,
  80,
  window.location.pathname === '/pedidos' ?
    fichaTecnicaAction.PEDIDO_FICHATECNICA :
    fichaTecnicaAction.DASHCLIENTE_FICHATECNICA,
  undefined,
  undefined,
  undefined,
  undefined,
  'datasheet-action-button'
);

const recordActionButonExpressa = ModelButton(
  'Ficha técnica',
  iconRecordExpressa,
  (data: IOrderItem) =>
    _downloadService.OpenFileFromOrderItem(IDownloadTypes.RECORD, data),
  'Ficha técnica não disponível',
  80,
  window.location.pathname === '/pedidos' ?
    fichaTecnicaAction.PEDIDO_FICHATECNICA :
    fichaTecnicaAction.DASHCLIENTE_FICHATECNICA
);

const reportActionButon = ModelButton(
  'Laudos técnicos',
  iconRecord,
  (data: IOrderItem) =>
    _downloadService.OpenFileFromOrderItem(IDownloadTypes.REPORT, data),
  eventName.LAUDO_TECNICO,
  80,
  window.location.pathname === '/pedidos' ?
    laudoTecnicoAction.PEDIDO_LAUDOTECNICO :
    laudoTecnicoAction.DASHCLIENTE_LAUDOTECNICO,
  undefined,
  undefined,
  undefined,
  undefined,
  'technical-report-action-button'
);

const reportActionButonExpressa = ModelButton(
  'Laudos técnicos',
  iconRecordExpressa,
  (data: IOrderItem) =>
    _downloadService.OpenFileFromOrderItem(IDownloadTypes.REPORT, data),
  'Laudo técnico não disponível',
  80,
  window.location.pathname === '/pedidos' ?
    laudoTecnicoAction.PEDIDO_LAUDOTECNICO :
    laudoTecnicoAction.DASHCLIENTE_LAUDOTECNICO
);

const labelCustomer = {
  label: 'Cliente',
  size: 210,
  key: 'company.companyName',
  noFilter: true,
};

const labelCustomerExpressa = {
  label: 'Paciente',
  size: 210,
  key: 'invoiceShipping.customerName',
  noFilter: true,
};

const labelItemNumber = { label: 'Item', key: 'number', size: 80, maxSize: 80, noFilter: true };

const labelProtheusOrderSale = {
  label: 'Pedido',
  key: 'protheusOrderSale',
  size: 80,
  maxSize: 80,
};

const labelMoneyOrderSale = {
  label: 'Pedido',
  key: 'moneyOrderSale',
  size: 80,
  maxSize: 80,
};

const labelOrderSale = {
  label: 'Pedido',
  key: 'orderSale',
  size: 80,
  maxSize: 80,
};

const labelOrderNumber = {
  label: 'Pedido',
  key: 'invoiceShipping.orderNumber',
  size: 90,
  maxSize: 90,
};

const labelOrderDate = {
  label: 'Dt do pedido',
  key: 'orderDate',
  type: 'date',
  size: 95,
  maxSize: 95,
};

const labelExpectedDate = {
  label: 'Previsão de faturamento',
  key: 'expectedBillingDate',
  type: 'date',
  size: 110,
  maxSize: 110,
};

const labelInvoiceNumber = {
  label: 'Nota Fiscal',
  key: 'invoiceNumber',
  size: 105,
  maxSize: 105,
  Component: (data: IOrders) => (
    <>
      <Text size={12} className="color-blue-gray text-wrap">
        {data.invoiceNumber || ''}
        <br />
        &nbsp;/&nbsp;
        {data.invoiceSerialNumber || ''}
      </Text>
    </>
  ),
};

const labelnumberShipping = {
  label: 'Número Remessa',
  key: 'invoiceShipping.invoiceNumber',
  size: 105,
  maxSize: 105,
  Component: (data: IOrders) => (
    <>
      <Text size={12} className="color-blue-gray text-wrap">
        {data.invoiceShipping?.invoiceNumber || ''}
        <br />
        &nbsp;/&nbsp;
        {data.invoiceShipping?.invoiceSerialNumber || ''}
      </Text>
    </>
  )
};

const labelSituation = {
  label: 'Situação',
  key: 'status',
  size: 90,
  Component: (data: IOrders) => (
    <>
      <Text size={12} className="color-blue-gray text-wrap" showTip>
        {Capitalize(data.status) ?? ''}
      </Text>
      {(data?.carbonEmission && data?.carbonEmission.length > 0) &&
        data?.carbonEmission !== 'Emite' &&
        <>
          <img
            src={iconGreenDelivery}
            className="pl-0313r"
            data-tooltip-content="Esse pedido foi entregue usando nossa frota de veículos elétricos"
          />
          <Tooltip variant="light" />
        </>
      }
    </>
  ),
};

const labelTotalOrderValue = {
  label: 'Valor',
  key: 'totalOrderValue',
  type: 'currency',
  size: 120,
  maxSize: 120,
};

const labelUF = {
  label: 'UF do cliente',
  key: 'uf',
  noFilter: true,
  size: 50,
  maxSize: 50,
  Component: (data: any) => (
    <Text className="color-blue-gray" size={12}>
      {data.company?.uf}
    </Text>
  ),
};

const labelItems = {
  label: 'Itens',
  key: 'items',
  noFilter: true,
  size: 50,
  maxSize: 50,
  Component: (data: any) => (
    <Text className="color-blue-gray" size={12}>
      {data.items?.length}
    </Text>
  ),
};

const labelPurchaseOrder = {
  label: 'Ordem de compra',
  key: 'buyOrder',
  noFilter: false,
  size: 100,
  maxSize: 90,
};

const labelPaymentMethod = {
  label: 'Condição de pgto',
  key: 'paymentMethod',
  noFilter: true,
  size: 90,
  maxSize: 90,
};

const labelBillingDate = {
  label: 'Dt de faturam.',
  key: 'billingDate',
  size: 95,
  maxSize: 95,
  Component: (data: IOrders) => {
    const value = (data as any)?.billingDate as string | Date | undefined;
    let output = '';

    try {
      if (value) {
        const date = typeof value === 'string' ? new Date(value) : (value as Date);
        
        if (!Number.isNaN(date.getTime())) {
          const dd = String(date.getUTCDate()).padStart(2, '0');
          const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
          const yyyy = String(date.getUTCFullYear());
          output = `${dd}/${mm}/${yyyy}`;
        }
      } 
    } catch {
      output = '';
    }

    return (
      <Text size={12} className="color-blue-gray">
        {output}
      </Text>
    );
  }
};

const labelDeadline = {
  label: 'Previsão de Entrega',
  type: 'date',
  key: 'expectedDeliveryDate',
  size: 120,
  maxSize: 120,
};

const enterpriseNameOrder = (data: IOrders) => (
  data.subsidiaryName && data.enterpriseCode === 22 ? data.subsidiaryName?.split(' ')[0] : data.enterpriseName
);

const labelEnterprise = {
  label: 'Empresa',
  key: 'enterpriseName',
  size: 90,
  maxSize: 90,
  Component: (data: IOrders) => (
    <Text
      className="color-blue-gray text-wrap"
      size={12}
      showTip
      showTipContent={enterpriseNameOrder(data)}>
      {enterpriseNameOrder(data)}
    </Text>
  )
};

const ordersModelBillingInvoice: Array<ITableModel> = [
  { ...labelProtheusOrderSale, size: 100, maxSize: 100 },
  { ...labelOrderDate, size: 100, maxSize: 100 },
  { ...labelInvoiceNumber, size: 100, maxSize: 100 },
  { ...labelSituation, size: 100 },
  { ...labelTotalOrderValue, size: 120, maxSize: 120 },
];

const ordersModelProtheusBasic: Array<ITableModel> = [
  labelProtheusOrderSale,
  labelOrderDate,
  labelInvoiceNumber,
  labelSituation,
  labelTotalOrderValue,
];

const ordersModelDashPending: Array<ITableModel> = [
  labelMoneyOrderSale,
  labelOrderDate,
  labelSituation,
  labelExpectedDate,
  labelTotalOrderValue,
];

const ordersModelDash: Array<ITableModel> = [
  ...ordersModelProtheusBasic,
  invoiceActionButon(),
  billetActionButton(),
  xmlActionButon(),
];

const ordersModelBilling: Array<ITableModel> = [
  ...ordersModelBillingInvoice,
  billetActionButton('Boleto', 80),
];

const ordersModelBillingSlider: Array<ITableModel> = [
  ...ordersModelBillingInvoice,
  billetActionButtonSlider('Boleto', 80),
];

const ordersModelTracking: Array<ITableModel> = [
  { ...labelProtheusOrderSale, size: 100, maxSize: 100 },
  { ...labelOrderDate, size: 130, maxSize: 130 },
  { ...labelInvoiceNumber, size: 120, maxSize: 120 },
  { ...labelSituation, size: 80 },
  { ...labelTotalOrderValue, size: 120, maxSize: 120 },
  trackingActionButon('Comprovante de Entrega', 100),
];

const ordersModelTrackingSlider: Array<ITableModel> = [
  { ...labelProtheusOrderSale, size: 100, maxSize: 100 },
  { ...labelOrderDate, size: 130, maxSize: 130 },
  { ...labelInvoiceNumber, size: 110, maxSize: 110 },
  { ...labelSituation, size: 80 },
  { ...labelTotalOrderValue, size: 130, maxSize: 130 },
  trackingActionButonSlider('Comprovante de Entrega', 100),
];

const noteCorrectionModelSlider: Array<ITableModel> = [
  { ...labelProtheusOrderSale, size: 100, maxSize: 100 },
  { ...labelOrderDate, size: 130, maxSize: 130 },
  { ...labelInvoiceNumber, size: 110, maxSize: 110 },
  { ...labelSituation, size: 80 },
  { ...labelTotalOrderValue, size: 130, maxSize: 130 },
  /*noteCorrectionActionButonSlider('Nota de correção', 100),*/
];

const ordersModelTrackingOrder: Array<ITableModel> = [
  { ...labelProtheusOrderSale, size: 100, maxSize: 100 },
  { ...labelOrderDate, size: 130, maxSize: 130 },
  { ...labelInvoiceNumber, size: 110, maxSize: 110 },
  { ...labelSituation, size: 80 },
  { ...labelTotalOrderValue, size: 130, maxSize: 130 },
  trackingOrderActionButon('', 100),
];

const ordersModelInvoice: Array<ITableModel> = [
  ...ordersModelBillingInvoice,
  invoiceActionButon('Nota Fiscal', 80),
];

const ordersModelInvoiceSlider: Array<ITableModel> = [
  ...ordersModelBillingInvoice,
  invoiceActionButonSlider('Nota Fiscal', 80),
];

const ordersModelXmlNfeSlider: Array<ITableModel> = [
  ...ordersModelBillingInvoice,
  xmlActionButon('XML', 80),
];

const ordersModelReport: Array<ITableModel> = [...ordersModelProtheusBasic];

const ordersItemsModel: Array<ITableModel> = [
  labelItemNumber,
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
  { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
  {
    label: 'Dt. de cancelamento',
    key: 'cancelationDate',
    type: 'date',
    size: 120,
    maxSize: 120,
  },
  recordActionButon,
];

const ordersItemsModelMobile: Array<ITableModel> = [
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70, noFilter: true },
  { label: 'Produto', key: 'name', size: 240, noFilter: true },
];

const ordersItemsModelBilled: Array<ITableModel> = [
  labelItemNumber,
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
  { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
  { label: 'Lote', key: 'lot', size: 90, maxSize: 90 },
  labelBillingDate,
  labelDeadline,
  {
    label: 'Dt. de cancelamento',
    key: 'cancelationDate',
    type: 'date',
    size: 100,
    maxSize: 100,
  },
  recordActionButon,
  reportActionButon,
];

const ordersItemsModelBilledSupplier: Array<ITableModel> = [
  labelItemNumber,
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
  { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
  { label: 'Lote', key: 'lot', size: 90, maxSize: 90 },
  labelBillingDate,
  labelDeadline,
  {
    label: 'Dt. de cancelamento',
    key: 'cancelationDate',
    type: 'date',
    size: 100,
    maxSize: 100,
  }
];

const ordersItemsModelExpressa: Array<ITableModel> = [
  labelItemNumber,
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
  { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
  { label: 'Lote', key: 'lot', size: 90, maxSize: 90 },
  labelBillingDate,
  labelDeadline,
  {
    label: 'Dt. de cancelamento',
    key: 'cancelationDate',
    type: 'date',
    size: 100,
    maxSize: 100,
  },
  /*recordActionButonExpressa,
  reportActionButonExpressa,*/
];

const ordersItemsModelReportSlider: Array<ITableModel> = [
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Lote', key: 'lot', size: 70, maxSize: 70 },
  recordActionButon,
  reportActionButon,
];

const ordersItemsModelOpenProtocol: Array<ITableModel> = [
  labelItemNumber,
  { label: 'Código', key: 'code', size: 80, maxSize: 80 },
  { label: 'Produto', key: 'name', size: 240 },
  { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
  { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
  { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
  { label: 'Lote', key: 'lot', size: 70, maxSize: 70 },
  {
    label: 'Dt. de cancelamento',
    key: 'cancelationDate',
    type: 'date',
    size: 120,
    maxSize: 120,
  },
];

const ordersItemsModelReviewOrderLost: Array<ITableModel> = [
  { label: 'Item', key: 'number', size: 40, maxSize: 50, noFilter: true },
  { label: "Código", key: "code", size: 80, maxSize: 80, noFilter: true },
  { label: "Produto", key: "name", size: 245, noFilter: true },
  { label: "Unidade", key: "unit", size: 70, maxSize: 75, noFilter: true },
  { label: "Qtd.", key: "quantity", size: 70, maxSize: 70, noFilter: true },

  {
    label: "Valor",
    key: "price",
    type: "currency",
    size: 125,
    maxSize: 125,
    noFilter: true
  },
  { label: "Lote", key: "lot", size: 90, maxSize: 90, noFilter: true },
];

const quantityBalance = (freezed: IOrders, data: IOrderItem) => {
  const freezedOrderItem = freezed.items?.find(
    (item: IOrderItem) => item.number === data.number
  );

  return (freezedOrderItem?.returnQuantity ?? 0);
};

export function getBalance(data: IOrderItem, freezedOrder: IOrders, isEditing?: boolean) {

  return (
    isEditing ?
      data.quantity - quantityBalance(freezedOrder, data) :
      data.balance + quantityBalance(freezedOrder, data)
  );
}

function setDefaultReturnQuantity(returnQuantity: number, balance: number) {
  return returnQuantity || balance;
}

function getItemReturnQuantity(item: any, order: any) {
  return order.items?.find((data: any) => data.number === item.number)
    ?.returnQuantity;
}

function renderInputQtd(item: any, props: any) {
  const { freezedOrder, order, onInputKeyDown, onInputLooseFocus } = props;
  const minBalance = getBalance(item, freezedOrder) >= item.returnMultiplier
    ? item.returnMultiplier
    : 0;

  return (
    order.items?.find((itemOrder: any) => itemOrder.number === item.number) ? (
      <InputThemer key={`themer-${item.number}`}>
        <input
          key={item.number}
          className="w-100"
          type="text"
          defaultValue={setDefaultReturnQuantity(
            getItemReturnQuantity(item, order),
            getBalance(item, freezedOrder)
          )}
          onBlur={(e) => onInputLooseFocus(e, item)}
          onKeyDown={onInputKeyDown}
          max={getBalance(item, freezedOrder)}
          min={minBalance}
          disabled={getBalance(item, freezedOrder) < item.returnMultiplier}
          step={item.returnMultiplier}
          id="missing-quantity"
        />
      </InputThemer>
    ) :
      (
        <Text className="color-blue-gray" size={12}>
          -
        </Text>
      )
  );
}

const shouldBeAbleToReturnProducts = (protocolCategory: string): boolean => {
  return TicketRule.isDevolution(protocolCategory);
};

const getReturnAmmountColumnTitle = (protocolCategory: string, ticketReason: string): string => {
  switch (protocolCategory) {
    case '7':
    case 'RECLAMAÇÃO TÉCNICA':
      return 'Volume Reclamado';

    case '1':
    case 'DEVOLUÇÃO':
      if (["61", "FALTA DE VOLUME"].includes(ticketReason.toString())) return 'Quantidade Faltante';

    default:
      return 'Devolução';
  }
};

const ordersItemsModelOpenProtocolDevolution = (props: any): Array<ITableModel> => {
  let model: Array<ITableModel> = [];
  const { freezedOrder, protocolCategory, ticketReason, isEditing } = props;

  const allColumns = [
    labelItemNumber,
    { label: 'Código', key: 'code', size: 80, maxSize: 80 },
    { label: 'Produto', key: 'name', size: 200 },
    { label: 'Und.', key: 'unit', size: 70, maxSize: 70 },
    { label: 'Qtd.', key: 'quantity', size: 70, maxSize: 70 },
    {
      label: 'Saldo',
      key: 'returnQuantity',
      size: 80,
      maxSize: 80,
      Component: (data: any) => (
        <Text className="color-blue-gray" size={12}>
          {getBalance(data, freezedOrder, isEditing)}
        </Text>
      ),
    },
    {
      label: getReturnAmmountColumnTitle(protocolCategory, ticketReason),
      noFilter: true,
      maxSize: 90,
      size: 90,
      Component: (data: any) => (renderInputQtd(data, props))
    },
    { label: 'Valor', key: 'price', type: 'currency', size: 115, maxSize: 115 },
    { label: 'Lote', key: 'lot', size: 70, maxSize: 70 },
    {
      label: 'Dt. de cancelamento',
      key: 'cancelationDate',
      type: 'date',
      size: 120,
      maxSize: 120,
    },
  ];

  if (shouldBeAbleToReturnProducts(protocolCategory)) model = allColumns;
  else model = allColumns.filter((c) => c.label !== 'Devolução' && c.label !== 'Volume Reclamado');

  return model;
};

const ordersModelCompletePending: Array<ITableModel> = [
  labelMoneyOrderSale,
  labelItems,
  labelCustomer,
  labelPurchaseOrder,
  //{ ...labelPaymentMethod, size: 120, maxSize: 120 },
  { ...labelOrderDate, size: 140, maxSize: 140, type: 'date/hour' },
  labelExpectedDate,
  labelSituation,
  labelTotalOrderValue,
];

const ordersModelDashShipping: Array<ITableModel> = [
  labelOrderNumber,
  labelOrderDate,
  labelnumberShipping,
  labelSituation,
  labelTotalOrderValue
];

const ordersModelCompleteShipping: Array<ITableModel> = [
  labelOrderNumber,
  labelnumberShipping,
  labelItems,
  labelCustomerExpressa,
  labelEnterprise,
  labelPurchaseOrder,
  labelOrderDate,
  labelBillingDate,
  labelDeadline,
  labelSituation,
  labelTotalOrderValue
];

const ordersModelComplete: Array<ITableModel> = [
  labelProtheusOrderSale,
  labelInvoiceNumber,
  labelCustomer,
  labelEnterprise,
  labelUF,
  labelPurchaseOrder,
  labelOrderDate,
  labelBillingDate,
  { ...labelDeadline, size: 105 },
  { ...labelSituation, size: 110 },
  labelTotalOrderValue,
];

const ordersModelOpenProtocol: Array<ITableModel> = [
  { ...labelOrderSale, noFilter: true },
  labelInvoiceNumber,
  labelItems,
  labelCustomer,
  labelPaymentMethod,
  labelOrderDate,
  labelBillingDate,
  labelDeadline,
  labelSituation,
  labelTotalOrderValue,
];

const ordersModelCompleteButton = (user?: IUser | undefined) => {
  const model: Array<ITableModel> = [
    ...ordersModelComplete,
    deliveryForecastButon('', undefined, user),
    invoiceActionButon(),
    billetActionButton('', undefined, user),
    xmlActionButon(),
    trackingActionButon(),
  ];

  return model;
};

const ordersModelCompleteButtonMobile = () => {
  const model: Array<ITableModel> = [
    { ...labelPurchaseOrder, size: 90, noFilter: true },
    {
      label: 'Cliente',
      key: 'company.companyName',
      size: 130,
      noFilter: true,
      Component: (data: any) => (
        <div className='w-100'>
          <OneLineText size={12} className="color-blue-gray text-wrap w-100">
            {data.company?.companyName || ''}
          </OneLineText>
          <Text size={12} className="color-blue-gray text-wrap w-100">
            {formatDocument(data.company?.documentNumber || '')}
          </Text>
        </div>
      )
    },
    { ...labelSituation, size: 110, noFilter: true },
  ];

  return model;
};

const ordersModelDashButtonShipping: Array<ITableModel> = [
  ...ordersModelDashShipping,
  invoiceShippingActionButon(),
  billetActionButton(),
  xmlShippingActionButon(),
  trackingShippingActionButon(),
];

const ordersModelCompleteButtonShipping: Array<ITableModel> = [
  ...ordersModelCompleteShipping,
  invoiceShippingActionButon(),
  billetActionButton(),
  xmlShippingActionButon(),
  trackingShippingActionButon(),
];

export {
  ordersItemsModelOpenProtocolDevolution,
  ordersModelDash,
  ordersModelDashPending,
  ordersModelBilling,
  ordersModelBillingSlider,
  ordersModelInvoice,
  ordersModelInvoiceSlider,
  ordersModelXmlNfeSlider,
  ordersModelComplete,
  ordersModelOpenProtocol,
  ordersItemsModelOpenProtocol,
  ordersItemsModelReviewOrderLost,
  ordersModelCompleteButton,
  ordersModelCompleteButtonMobile,
  ordersModelCompleteButtonShipping,
  ordersModelDashButtonShipping,
  ordersModelCompletePending,
  ordersModelCompleteShipping,
  ordersModelTracking,
  ordersModelTrackingSlider,
  noteCorrectionModelSlider,
  ordersModelTrackingOrder,
  ordersModelReport,
  ordersItemsModel,
  ordersItemsModelBilled,
  ordersItemsModelBilledSupplier,
  ordersItemsModelMobile,
  ordersItemsModelReportSlider,
  ordersItemsModelExpressa,
  recordActionButon,
  reportActionButon,
  pendingFilter,
  billedFilter,
  shippingFilter
};